import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    type Injector,
    runInInjectionContext,
    signal,
    type Signal,
} from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { assertInjector, qpxToast as toast } from '@quipex/core/utils';
import {
    injectQuippySession,
    type QuippyChatRef,
    type QuippyScope,
    type QuippyScopeSelection,
} from '@quipex/feature/quippy-conversation';
import { DialogService } from '@quipex/shared/ui';
import { take } from 'rxjs';
import { AppQuippyHostActions } from '../host-actions';
import type { AppQuippyDialog } from './dialog';
import { loadQuippyDialog } from './load-quippy-dialog';

export interface AppQuippyDialogLauncher {
    readonly isOpen: Signal<boolean>;
    open(selection: QuippyScopeSelection, ref?: QuippyChatRef): Promise<void>;
    close(): void;
}

export interface AppQuippyDialogOptions {
    readonly injector?: Injector;
    readonly focusFallback?: () => void;
}

export function injectAppQuippyDialog(
    options: AppQuippyDialogOptions = {}
): AppQuippyDialogLauncher {
    const injector = assertInjector(injectAppQuippyDialog, options.injector);
    const destroyRef = injector.get(DestroyRef);
    const document = injector.get(DOCUMENT);
    const dialogService = injector.get(DialogService);
    const isOpen = signal(false);
    const session = runInInjectionContext(injector, injectQuippySession);
    let dialogRef: MatDialogRef<AppQuippyDialog> | undefined;
    let opening: Promise<void> | undefined;
    let generation = 0;
    let destroyed = false;
    let focusOrigin: HTMLElement | null = null;

    const showRef = (ref: QuippyChatRef): void => {
        dialogRef?.componentRef?.setInput('chatRef', ref);
    };

    const close = (): void => {
        generation += 1;
        opening = undefined;
        const active = dialogRef;
        dialogRef = undefined;
        isOpen.set(false);
        if (active) {
            const closedGeneration = generation;
            const origin = focusOrigin;
            active
                .afterClosed()
                .pipe(take(1))
                .subscribe(() => {
                    if (!destroyed && generation === closedGeneration)
                        restoreFocus(origin, options.focusFallback);
                });
            active.close();
        }
    };

    destroyRef.onDestroy(() => {
        destroyed = true;
        close();
    });

    const open = async (
        selection: QuippyScopeSelection,
        ref?: QuippyChatRef
    ): Promise<void> => {
        if (destroyed) return;
        if (dialogRef) {
            if (ref) showRef(ref);
            return;
        }
        if (opening) {
            await opening;
            if (ref) showRef(ref);
            return;
        }

        const scope = scopeFromSelection(selection);
        if (selection.kind === 'building') {
            session.updateBuilding(scope, selection.building);
        }

        focusOrigin = getFocusOrigin(document);
        const origin = focusOrigin;
        const requestGeneration = ++generation;
        const openingPromise = loadQuippyDialog()
            .then((Dialog) => {
                if (destroyed || requestGeneration !== generation) return;
                const opened = runInInjectionContext(injector, () =>
                    dialogService.openDialog(
                        {
                            component: Dialog,
                            componentInputs: { scope, chatRef: ref ?? null },
                        },
                        {
                            ariaLabel: 'Ask Quippy',
                            autoFocus: 'textarea',
                            backdropClass: 'qpx-sidesheet-backdrop',
                            closeOnNavigation: false,
                            disableClose: false,
                            injector,
                            maxWidth: '100dvw',
                            panelClass: [
                                'qpx-sidesheet',
                                'flex-content',
                                'no-padding',
                                'no-scroll',
                            ],
                            position: {
                                right: 'var(--sidesheet-viewport-margin)',
                                top: 'var(--sidesheet-viewport-margin)',
                            },
                            restoreFocus: false,
                            width: '33rem',
                        }
                    )
                );
                dialogRef = opened;
                isOpen.set(true);
                opened
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe(() => {
                        if (dialogRef !== opened) return;
                        dialogRef = undefined;
                        isOpen.set(false);
                        if (!destroyed)
                            restoreFocus(origin, options.focusFallback);
                    });
            })
            .catch(() => {
                if (!destroyed && requestGeneration === generation) {
                    // Dynamic imports have no HTTP ProblemDetails auto-toast.
                    // eslint-disable-next-line agentic/prefer-auto-error-toast
                    toast.error('Unable to open Ask Quippy. Try again.');
                }
            })
            .finally(() => {
                if (opening === openingPromise) opening = undefined;
            });
        opening = openingPromise;
        await openingPromise;
    };

    const dialog = { isOpen: isOpen.asReadonly(), open, close };
    injector.get(AppQuippyHostActions, null)?.registerDialog(dialog);
    return dialog;
}

function scopeFromSelection(selection: QuippyScopeSelection): QuippyScope {
    return selection.kind === 'portfolio'
        ? selection
        : { kind: 'building', buildingId: selection.building.id };
}

function getFocusOrigin(document: Document): HTMLElement | null {
    const activeElement = document.activeElement;
    const HTMLElementConstructor = document.defaultView?.HTMLElement;
    return HTMLElementConstructor &&
        activeElement instanceof HTMLElementConstructor
        ? activeElement
        : null;
}

function restoreFocus(
    focusOrigin: HTMLElement | null,
    fallback: (() => void) | undefined
): void {
    if (focusOrigin?.isConnected) {
        focusOrigin.focus();
        return;
    }
    fallback?.();
}
